<template>
  <div class="entries-container page-container bg-light-gradient">
    <div class="container-padded py-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-clipboard-list"></i></span> Test-Einträge
        </h1>
        <div class="d-flex gap-2">
          <button 
            class="btn btn-outline-primary btn-with-icon animate-fade-in with-hover-effect"
            @click="exportEntries"
          >
            <span class="icon me-2"><i class="fas fa-file-export"></i></span> Exportieren
          </button>
          <router-link :to="userIsSuperAdmin ? '/admin/dashboard' : '/company/dashboard'" class="btn btn-outline-secondary btn-with-icon animate-fade-in with-hover-effect">
            <span class="icon me-2"><i class="fas fa-arrow-left"></i></span> Zurück zum Dashboard
          </router-link>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <Card animateIn="fade-in-up" class="card-with-table">
            <template v-slot:header>
              <div class="d-flex justify-content-between align-items-center flex-wrap">
                <h4 class="card-title mb-0">Einträge</h4>
                <div class="d-flex flex-wrap align-items-center gap-2">
                  <div class="search-group with-focus-effect">
                    <span class="search-icon">
                      <i class="fas fa-search"></i>
                    </span>
                    <input 
                      type="text" 
                      class="form-control search-input" 
                      placeholder="Suche nach Namen..." 
                      v-model="filters.search"
                      @input="debounceSearch"
                    >
                    <button 
                      v-if="filters.search" 
                      class="btn-icon btn-icon-muted clear-search-btn" 
                      @click="clearSearch"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  
                  <div class="select-group with-focus-effect">
                    <select 
                      class="form-select" 
                      v-model="filters.test_id"
                      @change="applyFilters"
                    >
                      <option value="">Alle Tests</option>
                      <option v-for="test in tests || []" :key="test?.id || index" :value="test?.id">
                        {{ test?.name || 'Unbekannter Test' }}
                      </option>
                    </select>
                  </div>
                  
                  <div class="select-group with-focus-effect">
                    <select 
                      class="form-select" 
                      v-model="filters.sort_by"
                      @change="applyFilters"
                    >
                      <option value="created_at">Datum (neueste zuerst)</option>
                      <option value="created_at_asc">Datum (älteste zuerst)</option>
                      <option value="score">Punkte (höchste zuerst)</option>
                      <option value="score_asc">Punkte (niedrigste zuerst)</option>
                      <option value="name">Name (A-Z)</option>
                      <option value="name_desc">Name (Z-A)</option>
                    </select>
                  </div>
                  
                  <button 
                    class="btn btn-outline-secondary btn-with-icon with-hover-effect" 
                    @click="resetFilters"
                  >
                    <span class="icon me-2"><i class="fas fa-redo-alt"></i></span> Zurücksetzen
                  </button>
                </div>
              </div>
            </template>

            <div v-if="loading" class="text-center py-5 content-loading">
              <div class="loader loader-lg mb-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Wird geladen...</span>
                </div>
              </div>
              <p class="text-muted">Einträge werden geladen...</p>
            </div>

            <div v-else-if="entries.length === 0" class="text-center py-5 content-empty">
              <span class="icon icon-muted icon-xxl d-block mb-3"><i class="fas fa-inbox"></i></span>
              <p class="lead text-muted mb-4">Keine Test-Einträge gefunden.</p>
              <p v-if="hasActiveFilters" class="text-muted">
                Versuchen Sie andere Filtereinstellungen oder 
                <a href="#" class="link-primary with-underline-effect" @click.prevent="resetFilters">setzen Sie alle Filter zurück</a>.
              </p>
            </div>

            <div v-else>
              <div class="table-responsive">
                <table class="table table-hover table-align-middle">
                  <thead>
                    <tr>
                      <th scope="col" width="30">#</th>
                      <th scope="col">Name</th>
                      <th scope="col">Test</th>
                      <th scope="col">Datum</th>
                      <th scope="col" width="150">Punkte</th>
                      <th scope="col" width="100">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr 
                      v-for="(entry, index) in (Array.isArray(entries) ? entries.filter(e => e && e.id) : [])" 
                      :key="entry.id"
                      class="data-row with-transition"
                      @click="viewEntryDetails(entry)"
                      style="cursor: pointer;"
                    >
                      <td class="fw-medium">{{ (pagination.current_page - 1) * pagination.per_page + index + 1 }}</td>
                      <td>
                        <div class="d-flex align-items-center">
                          <div>
                            <p class="mb-0 fw-medium">{{ entry.first_name ? entry.title ? entry.title + ' ' + entry.first_name + ' ' + entry.last_name :  entry.first_name + ' ' + entry.last_name : 'Unbekannter Benutzer' }}</p>
                            <p class="mb-0 small text-muted">{{ entry.email || '-' }}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span class="badge bg-primary with-hover-effect">{{ entry.test?.name || 'Unbekannt' }}</span>
                      </td>
                      <td>
                        <p class="mb-0">{{ formatDate(entry.created_at) }}</p>
                      </td>
                      <td>
                        <div>
                          <div class="d-flex justify-content-between mb-1">
                            <span class="fw-medium">{{ entry.score || 0 }} / {{ entry.max_score || 0 }}</span>
                            <span class="badge" :class="getScoreBadgeClass(calculatePercentage(entry.score, entry.max_score))">
                              {{ calculatePercentage(entry.score, entry.max_score) }}%
                            </span>
                          </div>
                          <div class="progress progress-slim">
                            <div 
                              class="progress-bar" 
                              :class="getScoreProgressClass(calculatePercentage(entry.score, entry.max_score))"
                              :style="{ width: calculatePercentage(entry.score, entry.max_score) + '%' }"
                              role="progressbar" 
                              :aria-valuenow="calculatePercentage(entry.score, entry.max_score)"
                              aria-valuemin="0" 
                              aria-valuemax="100"
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div class="d-flex gap-1">
                          <button 
                            class="btn-icon btn-icon-primary with-tooltip"
                            @click.stop="viewEntryDetails(entry)"
                            data-tooltip="Details anzeigen"
                          >
                            <i class="fas fa-eye"></i>
                          </button>
                          <button 
                            class="btn-icon btn-icon-danger with-tooltip"
                            @click.stop="confirmDeleteEntry(entry)"
                            data-tooltip="Löschen"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div class="footer-container">
                <div class="d-flex justify-content-between align-items-center flex-wrap">
                  <p class="mb-md-0 text-muted">
                    Zeige {{ entries.length }} von {{ pagination.total }} Einträgen
                  </p>
                  
                  <nav v-if="pagination.last_page > 1" aria-label="Page navigation">
                    <ul class="pagination pagination-with-animation mb-0">
                      <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                        <a 
                          class="page-link with-hover-effect" 
                          href="#" 
                          @click.prevent="changePage(pagination.current_page - 1)"
                          aria-label="Previous"
                        >
                          <span aria-hidden="true">&laquo;</span>
                        </a>
                      </li>
                      <li 
                        v-for="pageNumber in getPaginationRange()" 
                        :key="pageNumber" 
                        class="page-item"
                        :class="{ active: pagination.current_page === pageNumber }"
                      >
                        <a 
                          class="page-link with-hover-effect" 
                          href="#" 
                          @click.prevent="changePage(pageNumber)"
                        >
                          {{ pageNumber }}
                        </a>
                      </li>
                      <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                        <a 
                          class="page-link with-hover-effect" 
                          href="#" 
                          @click.prevent="changePage(pagination.current_page + 1)"
                          aria-label="Next"
                        >
                          <span aria-hidden="true">&raquo;</span>
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
    
    <!-- Löschen bestätigen Modal -->
    <b-modal
      id="delete-entry-modal"
      ref="deleteModal"
      title="Eintrag löschen"
      ok-title="Löschen"
      cancel-title="Abbrechen"
      ok-variant="danger"
      @ok="deleteEntryConfirmed"
      centered
    >
      <p class="my-4">
        Sind Sie sicher, dass Sie diesen Testeintrag löschen möchten?
      </p>
      <p class="text-danger">
        <span class="icon icon-danger me-2"><i class="fas fa-exclamation-triangle"></i></span>
        Diese Aktion kann nicht rückgängig gemacht werden!
      </p>
    </b-modal>
    
    <!-- Export Modal -->
    <b-modal
      id="export-modal"
      ref="exportModal"
      title="Einträge exportieren"
      ok-title="Exportieren"
      cancel-title="Abbrechen"
      @ok="performExport"
      centered
    >
      <div class="modal-form">
        <div class="mb-4">
          <label class="form-label label-emphasized">Format</label>
          <div class="d-flex gap-3">
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="radio" 
                id="formatCsv" 
                value="csv" 
                v-model="exportOptions.format"
              >
              <label class="form-check-label" for="formatCsv">
                <span class="icon icon-primary me-1"><i class="fas fa-file-csv"></i></span> CSV
              </label>
            </div>
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="radio" 
                id="formatXlsx" 
                value="xlsx" 
                v-model="exportOptions.format"
              >
              <label class="form-check-label" for="formatXlsx">
                <span class="icon icon-success me-1"><i class="fas fa-file-excel"></i></span> Excel (XLSX)
              </label>
            </div>
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="radio" 
                id="formatPdf" 
                value="pdf" 
                v-model="exportOptions.format"
              >
              <label class="form-check-label" for="formatPdf">
                <span class="icon icon-danger me-1"><i class="fas fa-file-pdf"></i></span> PDF
              </label>
            </div>
          </div>
        </div>
        
        <div class="mb-4">
          <label class="form-label label-emphasized">Datensatz</label>
          <div class="d-flex gap-3">
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="radio" 
                id="scopeFiltered" 
                value="filtered" 
                v-model="exportOptions.scope"
              >
              <label class="form-check-label" for="scopeFiltered">
                Nur gefilterte Einträge ({{ pagination.total }})
              </label>
            </div>
            <div class="form-check">
              <input 
                class="form-check-input" 
                type="radio" 
                id="scopeAll" 
                value="all" 
                v-model="exportOptions.scope"
              >
              <label class="form-check-label" for="scopeAll">
                Alle Einträge
              </label>
            </div>
          </div>
        </div>
        
        <div class="mb-4">
          <label class="form-label label-emphasized">Felder</label>
          <div class="row">
            <div class="col-md-6">
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="fieldId" 
                  v-model="exportOptions.fields.id"
                >
                <label class="form-check-label" for="fieldId">
                  ID
                </label>
              </div>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="fieldName" 
                  v-model="exportOptions.fields.name"
                >
                <label class="form-check-label" for="fieldName">
                  Benutzer-Name
                </label>
              </div>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="fieldEmail" 
                  v-model="exportOptions.fields.email"
                >
                <label class="form-check-label" for="fieldEmail">
                  E-Mail
                </label>
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="fieldTest" 
                  v-model="exportOptions.fields.test"
                >
                <label class="form-check-label" for="fieldTest">
                  Test
                </label>
              </div>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="fieldScore" 
                  v-model="exportOptions.fields.score"
                >
                <label class="form-check-label" for="fieldScore">
                  Punkte / Ergebnis
                </label>
              </div>
              <div class="form-check">
                <input 
                  class="form-check-input" 
                  type="checkbox" 
                  id="fieldDate" 
                  v-model="exportOptions.fields.date"
                >
                <label class="form-check-label" for="fieldDate">
                  Datum
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Card from '@/components/ui/Card.vue';
import { debounce } from 'lodash';
import { useAuthStore } from '@/stores/auth';
import { useTestsStore } from '@/stores/tests';
import { useEntriesStore } from '@/stores/entries';

export default {
  name: 'EntriesList',
  components: {
    Card
  },
  setup() {
    const authStore = useAuthStore();
    const testsStore = useTestsStore();
    const entriesStore = useEntriesStore();
    
    return {
      authStore,
      testsStore,
      entriesStore
    };
  },
  data() {
    return {
      loading: true,
      // Für die Filter-Funktionalität
      filters: {
        search: '',
        test_id: '',
        sort_by: 'created_at'
      },
      // Für die Pagination
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
      },
      // Für den Export
      exportOptions: {
        format: 'csv',
        scope: 'filtered',
        fields: {
          id: true,
          name: true,
          email: true,
          test: true,
          score: true,
          date: true
        }
      },
      // Für die Löschbestätigung
      entryToDelete: null
    };
  },
  computed: {
    entries() {
      const entriesList = this.entriesStore.entriesList;
      return Array.isArray(entriesList) ? entriesList : [];
    },
    tests() {
      return this.testsStore.getAllTests || [];
    },
    paginationInfo() {
      return this.entriesStore.paginationInfo || {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
      };
    },
    filtersActive() {
      return this.entriesStore.filtersActive || false;
    },
    user() {
      return this.authStore.user || null;
    },
    hasActiveFilters() {
      return this.filtersActive || this.filters.search || this.filters.test_id;
    },
    userIsSuperAdmin() {
      return this.user && this.user.role === 'super_admin';
    }
  },
  watch: {
    paginationInfo: {
      handler(newPagination) {
        if (newPagination) {
          this.pagination = { ...newPagination };
        }
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    this.loadData();
    // Debounce-Funktion für die Suche einrichten
    this.debounceSearch = debounce(() => {
      this.applyFilters();
    }, 300);
    
    // Debug-Logs hinzufügen
    console.log('EntryList created, entries:', this.entries);
    console.log('EntryList store entries:', this.entriesStore.entriesList);
  },
  methods: {
    // Daten laden
    async loadData() {
      this.loading = true;
      
      try {
        console.log('loadData: Beginne Laden der Daten');
        
        // Tests für die Filter-Dropdown laden
        await this.testsStore.fetchTests();
        console.log('loadData: Tests geladen, Ergebnis:', this.testsStore.getAllTests);
        
        // Einträge laden
        await this.entriesStore.fetchEntries();
        console.log('loadData: Einträge geladen, Ergebnis:', this.entriesStore.entriesList);
        
        // Prüfen, ob die Einträge korrekt sind
        const entriesList = this.entriesStore.entriesList;
        if (!Array.isArray(entriesList)) {
          console.error('loadData: entriesList ist kein Array:', entriesList);
        } else {
          console.log('loadData: entriesList ist ein Array mit', entriesList.length, 'Elementen');
        }
      } catch (error) {
        console.error('Fehler beim Laden der Daten:', error);
      } finally {
        this.loading = false;
      }
    },
    // Filter anwenden
    applyFilters() {
      this.pagination.current_page = 1;
      
      this.entriesStore.setFilters({
        ...this.filters,
        page: this.pagination.current_page
      });
    },
    // Filter zurücksetzen
    resetFilters() {
      this.filters = {
        search: '',
        test_id: '',
        sort_by: 'created_at'
      };
      
      this.entriesStore.resetFilters();
    },
    // Suchfeld leeren
    clearSearch() {
      this.filters.search = '';
      this.applyFilters();
    },
    // Seite wechseln (Pagination)
    changePage(page) {
      if (page <= 0 || page > this.pagination.last_page) return;
      
      this.pagination.current_page = page;
      this.entriesStore.setPage(page);
    },
    // Pagination-Zahlen berechnen
    getPaginationRange() {
      const current = this.pagination.current_page || 1;
      const last = this.pagination.last_page || 1;
      
      // Sicherstellen, dass last nicht kleiner als 1 ist
      if (last < 1) return [1];
      
      if (last <= 7) {
        return Array.from({ length: last }, (_, i) => i + 1);
      }
      
      if (current <= 3) {
        return [1, 2, 3, 4, '...', last - 1, last];
      }
      
      if (current >= last - 2) {
        return [1, 2, '...', last - 3, last - 2, last - 1, last];
      }
      
      return [1, '...', current - 1, current, current + 1, '...', last];
    },
    // Datum formatieren
    formatDate(dateString) {
      const options = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleDateString('de-DE', options);
    },
    // Prozentsatz berechnen
    calculatePercentage(score, maxScore) {
      if (!score || !maxScore) return 0;
      return Math.round((score / maxScore) * 100);
    },
    // Badge-Klasse basierend auf Prozentsatz bestimmen
    getScoreBadgeClass(percentage) {
      if (percentage >= 80) return 'badge-success';
      if (percentage >= 60) return 'badge-primary';
      if (percentage >= 40) return 'badge-warning';
      return 'badge-danger';
    },
    // Progress-Bar-Klasse basierend auf Prozentsatz bestimmen
    getScoreProgressClass(percentage) {
      if (percentage >= 80) return 'bg-success';
      if (percentage >= 60) return 'bg-primary';
      if (percentage >= 40) return 'bg-warning';
      return 'bg-danger';
    },
    // Details-Ansicht öffnen
    viewEntryDetails(entry) {
      if (!entry || !entry.id) {
        this.$toast.error('Ungültiger Eintrag');
        return;
      }
      this.$router.push({ name: 'EntryDetail', params: { id: entry.id } });
    },
    // Löschen bestätigen
    confirmDeleteEntry(entry) {
      if (!entry || !entry.id) {
        this.$toast.error('Ungültiger Eintrag');
        return;
      }
      this.entryToDelete = entry;
      this.$refs.deleteModal.show();
    },
    // Eintrag löschen
    async deleteEntryConfirmed() {
      if (!this.entryToDelete) return;
      
      this.loading = true;
      
      try {
        await this.entriesStore.deleteEntry(this.entryToDelete.id);
        this.$toast.success('Eintrag erfolgreich gelöscht');
        
        // Modal schließen
        if (this.$refs.deleteModal) {
          this.$refs.deleteModal.hide();
        }
      } catch (error) {
        console.error('Fehler beim Löschen des Eintrags:', error);
        this.$toast.error('Fehler beim Löschen des Eintrags');
      } finally {
        this.loading = false;
        this.entryToDelete = null;
      }
    },
    // Export-Modal anzeigen
    exportEntries() {
      try {
        // Direkt das Modal über die Referenz öffnen
        if (this.$refs.exportModal) {
          this.$refs.exportModal.show();
        } else {
          // Fallback: Über Bootstrap Vue Modal
          this.$bvModal.show('export-modal');
        }
      } catch (error) {
        console.error('Fehler beim Öffnen des Export-Modals:', error);
        this.$toast.error('Export-Funktion ist derzeit nicht verfügbar');
      }
    },
    // Export durchführen
    async performExport() {
      try {
        this.loading = true;
        
        // Exportoptionen vorbereiten
        const exportData = {
          format: this.exportOptions.format,
          scope: this.exportOptions.scope,
          fields: Object.keys(this.exportOptions.fields).filter(key => this.exportOptions.fields[key]),
          filters: this.filters
        };
        
        console.log('Exportiere mit Optionen:', exportData);
        
        // Echten Export durchführen
        await this.performRealExport(exportData);
        
        this.$toast.success(`Einträge wurden erfolgreich als ${this.exportOptions.format.toUpperCase()} exportiert`);
        
        // Modal schließen
        if (this.$refs.exportModal) {
          this.$refs.exportModal.hide();
        } else {
          this.$bvModal.hide('export-modal');
        }
      } catch (error) {
        console.error('Fehler beim Exportieren:', error);
        this.$toast.error('Fehler beim Exportieren der Einträge');
      } finally {
        this.loading = false;
      }
    },
    
    // Echten Export durchführen
    async performRealExport(exportData) {
      // Daten für Export vorbereiten
      const entriesToExport = this.exportOptions.scope === 'all' 
        ? await this.entriesStore.getAllEntries() 
        : this.entries;
      
      if (!entriesToExport || entriesToExport.length === 0) {
        throw new Error('Keine Daten zum Exportieren verfügbar');
      }
      
      // Daten formatieren
      const formattedData = this.formatDataForExport(entriesToExport, exportData.fields);
      
      // Datei erstellen und herunterladen
      if (exportData.format === 'csv') {
        this.downloadCSV(formattedData);
      } else if (exportData.format === 'xlsx') {
        await this.downloadXLSX(formattedData);
      } else if (exportData.format === 'pdf') {
        await this.downloadPDF(formattedData);
      }
    },
    
    // Daten für Export formatieren
    formatDataForExport(entries, fields) {
      const headers = [];
      const data = [];
      
      // Headers erstellen
      if (fields.includes('id')) headers.push('ID');
      if (fields.includes('name')) headers.push('Name');
      if (fields.includes('email')) headers.push('E-Mail');
      if (fields.includes('test')) headers.push('Test');
      if (fields.includes('score')) headers.push('Punkte');
      if (fields.includes('date')) headers.push('Datum');
      
      // Daten erstellen
      entries.forEach(entry => {
        const row = [];
        if (fields.includes('id')) row.push(entry.id);
        if (fields.includes('name')) row.push(entry.first_name + ' ' + entry.last_name);
        if (fields.includes('email')) row.push(entry.email);
        if (fields.includes('test')) row.push(entry.test ? entry.test.name : 'Unbekannt');
        if (fields.includes('score')) row.push(`${entry.score} / ${entry.max_score} (${Math.round((entry.score / entry.max_score) * 100)}%)`);
        if (fields.includes('date')) row.push(new Date(entry.created_at).toLocaleString('de-DE'));
        data.push(row);
      });
      
      return { headers, data };
    },
    
    // CSV Download
    downloadCSV(formattedData) {
      // BOM für UTF-8 hinzufügen (für korrekte Darstellung in Excel)
      const BOM = '\uFEFF';
      const csvContent = BOM + [
        formattedData.headers.join(';'),
        ...formattedData.data.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';'))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `test-eintraege-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    // XLSX Download
    async downloadXLSX(formattedData) {
      // XLSX.js Bibliothek verwenden (falls verfügbar)
      if (typeof XLSX === 'undefined') {
        // Fallback: CSV mit .xlsx Erweiterung
        const csvContent = [
          formattedData.headers.join('\t'),
          ...formattedData.data.map(row => row.map(cell => String(cell)).join('\t'))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `test-eintraege-${new Date().toISOString().split('T')[0]}.xlsx`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.$toast.info('Tab-separierte Datei wurde als .xlsx heruntergeladen. Sie können diese in Excel öffnen.');
        return;
      }
      
      const worksheet = XLSX.utils.aoa_to_sheet([formattedData.headers, ...formattedData.data]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Test-Einträge');
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `test-eintraege-${new Date().toISOString().split('T')[0]}.xlsx`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    // PDF Download
    async downloadPDF(formattedData) {
      // Einfache PDF-Erstellung ohne externe Bibliothek
      // Erstelle eine HTML-Tabelle und konvertiere sie zu PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Test-Einträge Export</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            tr:nth-child(even) { background-color: #f9f9f9; }
          </style>
        </head>
        <body>
          <h1>Test-Einträge Export</h1>
          <table>
            <thead>
              <tr>
                ${formattedData.headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${formattedData.data.map(row => 
                `<tr>${row.map(cell => `<td>${String(cell)}</td>`).join('')}</tr>`
              ).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;
      
      // Erstelle Blob und lade HTML-Datei herunter
      const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `test-eintraege-${new Date().toISOString().split('T')[0]}.html`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Hinweis für den Benutzer
      this.$toast.info('HTML-Datei wurde heruntergeladen. Sie können diese in Ihrem Browser als PDF drucken (Strg+P).');
    }
  }
};
</script>

<style lang="scss" scoped>
.entries-container {
  padding-top: calc(var(--admin-message-height, 48px) + 1rem);

  .container-padded {
    padding: 2rem;
  }

  .display-heading {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--bs-gray-900);
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .icon-circle {
      width: 42px;
      height: 42px;
      border-radius: 12px;
      background: var(--bs-primary-bg-subtle);
      color: var(--bs-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.25rem;
    }
  }

  .card-with-table {
    background: var(--bs-white);
    border: 1px solid var(--bs-gray-200);
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .card-header {
      padding: 1.5rem;
      border-bottom: 1px solid var(--bs-gray-200);
      background: var(--bs-white);

      .card-title {
        color: var(--bs-gray-900);
        font-size: 1.25rem;
        font-weight: 600;
      }
    }
  }

  .search-group {
    position: relative;
    min-width: 260px;

    .search-icon {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: var(--bs-gray-500);
      z-index: 2;
    }

    .search-input {
      padding-left: 2.75rem;
      height: 42px;
      border-radius: 8px;
      border: 1px solid var(--bs-gray-300);
      background: var(--bs-white);
      transition: all 0.2s ease;

      &:focus {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.1);
      }
    }

    .clear-search-btn {
      position: absolute;
      right: 0.75rem;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  .select-group {
    min-width: 200px;

    .form-select {
      height: 42px;
      border-radius: 8px;
      border: 1px solid var(--bs-gray-300);
      padding: 0.5rem 2.25rem 0.5rem 1rem;
      background-color: var(--bs-white);
      transition: all 0.2s ease;

      &:focus {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.1);
      }
    }
  }

  .table {
    margin: 0;

    th {
      background: var(--bs-gray-50);
      font-weight: 600;
      color: var(--bs-gray-700);
      padding: 1rem 1.5rem;
      border-bottom: 2px solid var(--bs-gray-200);
      white-space: nowrap;
    }

    td {
      padding: 1rem 1.5rem;
      vertical-align: middle;
      color: var(--bs-gray-900);
      border-bottom: 1px solid var(--bs-gray-200);
    }

    .data-row {
      transition: background-color 0.2s ease;

      &:hover {
        background-color: var(--bs-gray-50);
      }
    }
  }

  .badge {
    padding: 0.5rem 0.75rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 6px;

    &.badge-success {
      background-color: var(--bs-success-bg-subtle);
      color: var(--bs-success);
    }

    &.badge-primary {
      background-color: var(--bs-primary-bg-subtle);
      color: var(--bs-primary);
    }

    &.badge-warning {
      background-color: var(--bs-warning-bg-subtle);
      color: var(--bs-warning);
    }

    &.badge-danger {
      background-color: var(--bs-danger-bg-subtle);
      color: var(--bs-danger);
    }
  }

  .progress-slim {
    height: 6px;
    border-radius: 3px;
    background-color: var(--bs-gray-200);
    margin-top: 0.5rem;

    .progress-bar {
      border-radius: 3px;
      transition: width 0.6s ease;
    }
  }

  .btn-icon {
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    border: none;
    background: transparent;

    &.btn-icon-primary {
      color: var(--bs-primary);
      background-color: var(--bs-primary-bg-subtle);

      &:hover {
        background-color: var(--bs-primary);
        color: white;
        transform: translateY(-2px);
      }
    }

    &.btn-icon-danger {
      color: var(--bs-danger);
      background-color: var(--bs-danger-bg-subtle);

      &:hover {
        background-color: var(--bs-danger);
        color: white;
        transform: translateY(-2px);
      }
    }
  }

  .pagination {
    .page-item {
      margin: 0 0.25rem;

      .page-link {
        border-radius: 6px;
        padding: 0.5rem 0.75rem;
        color: var(--bs-gray-700);
        border: 1px solid var(--bs-gray-300);
        min-width: 36px;
        text-align: center;

        &:hover {
          background-color: var(--bs-primary-bg-subtle);
          color: var(--bs-primary);
          border-color: var(--bs-primary-bg-subtle);
        }
      }

      &.active .page-link {
        background-color: var(--bs-primary);
        border-color: var(--bs-primary);
        color: white;
      }

      &.disabled .page-link {
        background-color: var(--bs-gray-100);
        border-color: var(--bs-gray-200);
        color: var(--bs-gray-400);
      }
    }
  }

  // Dark Mode Styles
  .dark-mode & {
    .display-heading {
      color: var(--bs-light);
    }

    .card-with-table {
      background: var(--bs-dark);
      border-color: var(--bs-gray-700);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

      .card-header {
        background: var(--bs-dark);
        border-color: var(--bs-gray-700);
      }

      .card-title {
        color: var(--bs-light);
      }
    }

    .search-group .search-input,
    .select-group .form-select {
      background: var(--bs-gray-800);
      border-color: var(--bs-gray-700);
      color: var(--bs-light);

      &::placeholder {
        color: var(--bs-gray-500);
      }
    }

    .table {
      th {
        background: var(--bs-gray-800);
        color: var(--bs-gray-300);
        border-color: var(--bs-gray-700);
      }

      td {
        color: var(--bs-light);
        border-color: var(--bs-gray-700);
      }

      .data-row:hover {
        background-color: var(--bs-gray-800);
      }
    }

    .progress-slim {
      background-color: var(--bs-gray-700);
    }

    .pagination {
      .page-link {
        background-color: var(--bs-gray-800);
        border-color: var(--bs-gray-700);
        color: var(--bs-light);

        &:hover {
          background-color: var(--bs-gray-700);
          border-color: var(--bs-gray-600);
        }
      }

      .page-item.disabled .page-link {
        background-color: var(--bs-gray-900);
        border-color: var(--bs-gray-800);
        color: var(--bs-gray-600);
      }
    }
  }
}
</style> 